import { useLiveQuery } from "dexie-react-hooks";
import { Printer, RotateCcw, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { usePos } from "@/app/context";

/**
 * 「印刷失敗 1件」 (T07-25): failed prints stay in the Dexie queue after 3 automatic tries; the
 * banner lists them with the reason in plain words, 「再印刷」 (same bytes) and 閉じる. It never
 * blocks selling.
 */
export function PrintQueueBanner() {
  const { t } = useTranslation();
  const { db, printer } = usePos();
  const failed = useLiveQuery(
    () => db.printJobs.where("status").equals("failed").sortBy("createdAt"),
    [db],
  );
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const count = failed?.length ?? 0;
  if (count === 0) {
    return null;
  }
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex min-h-10 items-center gap-2 rounded-md bg-status-problem px-3 text-sm font-semibold text-white"
      >
        <Printer aria-hidden className="size-4" />
        {t("print.failed_banner", { count })}
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label={t("print.failed_banner", { count })}
          className="absolute right-0 top-12 z-40 w-[28rem] rounded-lg border bg-background p-3 shadow-xl"
        >
          <ul className="flex flex-col gap-2">
            {(failed ?? []).map((job) => (
              <li
                key={job.id}
                data-print-job={job.id}
                className="flex flex-col gap-2 rounded-md border p-2"
              >
                <p className="font-medium">{job.label}</p>
                <p className="text-sm text-pos-problem">
                  {t(`print.${job.lastError ?? "write_failed"}`, {
                    defaultValue: t("print.write_failed"),
                  })}
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => job.id !== undefined && void db.printJobs.delete(job.id)}
                    className="flex min-h-12 items-center gap-1 rounded-md border px-3"
                  >
                    <X aria-hidden className="size-4" />
                    {t("print.dismiss")}
                  </button>
                  <button
                    type="button"
                    disabled={busy === job.id}
                    onClick={() => {
                      if (job.id === undefined) {
                        return;
                      }
                      setBusy(job.id);
                      void printer.queue.retry(job.id).finally(() => setBusy(null));
                    }}
                    className="flex min-h-12 items-center gap-1 rounded-md bg-primary px-4 font-semibold text-primary-foreground disabled:opacity-50"
                  >
                    <RotateCcw aria-hidden className="size-4" />
                    {t("print.retry")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
